import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { CampaignService } from "src/app/services/campaign.service";
import { DeleteCampaignState } from "./delete-campaign.state";

const initialState: DeleteCampaignState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
};

@Injectable({
    providedIn: 'root'
})
export class DeleteCampaignComponentStore extends ComponentStore<DeleteCampaignState> {

    constructor(private store: Store<any>, private readonly campaignService: CampaignService) {
        super(initialState);
    }

    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (loaded, loading, success, errorMessage) => ({
            loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater(( _ : DeleteCampaignState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: DeleteCampaignState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: DeleteCampaignState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateDetailState = this.updater((state: DeleteCampaignState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
        };
    });

    readonly setError = this.updater((state: DeleteCampaignState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly deleteCampaign = this.effect((detailParams$: Observable<{ companyId: string, campaignId: number }>) => {
        this.setLoading();
        return detailParams$.pipe(
            switchMap((params => this.campaignService.deleteCampaign(params.companyId, params.campaignId).pipe(
                map(() => {
                    this.updateDetailState();
                    this.setLoaded();
                    this.setInitial();
                }
                ),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}